/*
 * Searay — storefront chat assistant
 * ----------------------------------------------------------------------------
 * The floating "Ask Ray" panel (snippets/searay-chat.liquid). Each message is one JSON
 * request to AttributeSyncApp through the Shopify App Proxy (/apps/chat/messages); the reply
 * brings text, product cards built from the catalogue, and cart actions.
 *
 * Cart actions are carried out here with the theme's own /cart/add.js, so the cart, its
 * discounts (AIOD) and checkout stay exactly as they are for any other add to cart. The
 * backend only ever returns an action for a logged-in shopper and a live in-stock variant.
 *
 * The conversation id lives in localStorage so the chat survives page changes; the history is
 * reloaded from the backend, which only returns it to the shopper it belongs to. Replayed
 * history never repeats its cart actions.
 *
 * A reply still being written when the shopper leaves the page (follows a product card, say) or
 * loses the connection is finished and saved by the backend anyway. The message waiting for it is
 * remembered for this tab, and the widget picks the reply up from the history, cart actions and
 * all. The widget makes the conversation id itself, so this works for a first message too.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-searay-chat]';
  var CONVERSATION_KEY = 'searay-chat-conversation';
  var OPEN_KEY = 'searay-chat-open';
  var PENDING_KEY = 'searay-chat-pending';
  var OPEN_CLASS = 'searay-chat--open';
  var ROLE_USER = 'user';
  var ROLE_ASSISTANT = 'assistant';
  var ACTION_ADD_TO_CART = 'addToCart';
  var CART_BUBBLE_SECTION = 'cart-icon-bubble';
  var CHECKOUT_URL = '/checkout';
  var CARD_IMAGE_WIDTH = 160;
  // The backend gives a turn 25 s; the proxy hop and a slow phone need some room on top.
  var REQUEST_TIMEOUT_MS = 40000;
  var POLL_INTERVAL_MS = 2000;
  // A reply that lands after the page changed still runs its cart actions, but not if the shopper
  // only comes back to the chat much later.
  var PENDING_MAX_AGE_MS = 10 * 60 * 1000;
  var CONVERSATION_ID_BYTES = 16;
  var MAX_INPUT_HEIGHT_PX = 120;
  var MESSAGE_SCROLL_MARGIN_PX = 12;

  var root = document.querySelector(ROOT_SELECTOR);
  if (!root) return;

  var endpoint = root.dataset.endpoint;
  var strings = JSON.parse(root.querySelector('[data-searay-chat-strings]').textContent);
  var panel = root.querySelector('.searay-chat__panel');
  var launcher = root.querySelector('[data-searay-chat-open]');
  var messages = root.querySelector('[data-searay-chat-messages]');
  var form = root.querySelector('[data-searay-chat-form]');
  var input = root.querySelector('[data-searay-chat-input]');
  var send = root.querySelector('[data-searay-chat-send]');
  var money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

  var conversationId = load(localStorage, CONVERSATION_KEY);
  var historyLoaded = false;
  // Bumped by reset(), so a history request still in flight cannot redraw the old conversation.
  var generation = 0;
  var busy = false;

  /* ── storage: may be unavailable (private mode, blocked site data) ── */

  function load(storage, key) {
    try {
      return storage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function save(storage, key, value) {
    try {
      if (value === null) storage.removeItem(key);
      else storage.setItem(key, value);
    } catch (e) {
      /* the chat still works for this page */
    }
  }

  /* ── conversation id and the message awaiting its reply ── */

  // Same shape as the backend's ids: 128 random bits as lower-case hex.
  function newConversationId() {
    var bytes = crypto.getRandomValues(new Uint8Array(CONVERSATION_ID_BYTES));
    return Array.prototype.map
      .call(bytes, function (b) {
        return ('0' + b.toString(16)).slice(-2);
      })
      .join('');
  }

  // pending: { text, sentAt } of the message awaiting its reply, or null once it has one.
  function savePending(pending) {
    save(
      sessionStorage,
      PENDING_KEY,
      pending && JSON.stringify({ conversationId: conversationId, text: pending.text, sentAt: pending.sentAt }),
    );
  }

  function loadPending() {
    try {
      var pending = JSON.parse(load(sessionStorage, PENDING_KEY));
      return pending && pending.conversationId === conversationId ? pending : null;
    } catch (e) {
      return null;
    }
  }

  // The history's answer to the pending message, or null if it has none yet. Matched on the text,
  // so an earlier reply is never taken for it (and its cart actions run again) when the history
  // is read before the backend has saved the pending message.
  function replyTo(messages, pending) {
    var n = messages.length;
    var last = messages[n - 1];
    var asked = messages[n - 2];
    return last && last.role === ROLE_ASSISTANT && asked && asked.role === ROLE_USER && asked.text === pending.text
      ? last
      : null;
  }

  /* ── open / close ── */

  function open(focus) {
    panel.hidden = false;
    root.classList.add(OPEN_CLASS);
    launcher.setAttribute('aria-expanded', 'true');
    save(sessionStorage, OPEN_KEY, '1');
    if (!historyLoaded) loadHistory();
    fitInput();
    if (focus) input.focus();
  }

  function close() {
    panel.hidden = true;
    root.classList.remove(OPEN_CLASS);
    launcher.setAttribute('aria-expanded', 'false');
    save(sessionStorage, OPEN_KEY, null);
    launcher.focus();
  }

  function reset() {
    generation += 1;
    historyLoaded = true;
    conversationId = null;
    save(localStorage, CONVERSATION_KEY, null);
    messages.textContent = '';
    greet();
    input.focus();
  }

  /* ── rendering ── */

  function scrollToEnd() {
    messages.scrollTop = messages.scrollHeight;
  }

  // A reply with cards is taller than the panel: show where it starts, not its last card.
  function scrollToStart(el) {
    messages.scrollTop = el.offsetTop - MESSAGE_SCROLL_MARGIN_PX;
  }

  // Scroll down only as far as needed for `el` to be fully visible.
  function reveal(el) {
    var bottom = el.offsetTop + el.offsetHeight + MESSAGE_SCROLL_MARGIN_PX - messages.clientHeight;
    if (bottom > messages.scrollTop) messages.scrollTop = bottom;
  }

  function bubble(kind) {
    var el = document.createElement('div');
    el.className = 'searay-chat__message searay-chat__message--' + kind;
    messages.appendChild(el);
    return el;
  }

  // "**bold**" inside a line, built as DOM nodes so nothing in a reply is ever parsed as HTML.
  function appendInline(parent, text) {
    text.split(/\*\*(.+?)\*\*/).forEach(function (part, i) {
      if (!part) return;
      if (i % 2) {
        var strong = document.createElement('strong');
        appendLinked(strong, part);
        parent.appendChild(strong);
      } else {
        appendLinked(parent, part);
      }
    });
  }

  // Addresses on this site become links: "https://searay.net.au/pages/faqs" or
  // "[our FAQs](https://searay.net.au/pages/faqs)". Any other address stays plain text, so a
  // reply can never link off the site.
  var LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g;

  function appendLinked(parent, text) {
    var last = 0;
    var match;
    LINK_PATTERN.lastIndex = 0;
    while ((match = LINK_PATTERN.exec(text))) {
      if (match.index > last) parent.appendChild(document.createTextNode(text.slice(last, match.index)));
      var link = siteLink(match[2] || match[3], match[1] || match[3]);
      parent.appendChild(link || document.createTextNode(match[0]));
      last = match.index + match[0].length;
    }
    if (last < text.length) parent.appendChild(document.createTextNode(text.slice(last)));
  }

  function siteLink(href, label) {
    var url;
    try {
      url = new URL(href, location.origin);
    } catch (e) {
      return null;
    }
    if (url.host !== location.host) return null;
    var a = document.createElement('a');
    a.className = 'searay-chat__link';
    a.href = url.href;
    a.textContent = label;
    return a;
  }

  // The model writes short paragraphs and "- " lists; render just that.
  function appendFormatted(parent, text) {
    text
      .trim()
      .split(/\n\s*\n/)
      .forEach(function (block) {
        var lines = block.split('\n').filter(function (l) {
          return l.trim();
        });
        var isList =
          lines.length &&
          lines.every(function (l) {
            return /^\s*[-*•]\s+/.test(l);
          });
        if (isList) {
          var ul = document.createElement('ul');
          lines.forEach(function (l) {
            var li = document.createElement('li');
            appendInline(li, l.replace(/^\s*[-*•]\s+/, ''));
            ul.appendChild(li);
          });
          parent.appendChild(ul);
        } else {
          var p = document.createElement('p');
          lines.forEach(function (l, i) {
            if (i) p.appendChild(document.createElement('br'));
            appendInline(p, l);
          });
          parent.appendChild(p);
        }
      });
  }

  function showUser(text) {
    bubble('user').textContent = text;
  }

  function showAssistant(text, products) {
    var el = bubble('assistant');
    appendFormatted(el, text);
    if (products && products.length) showCards(products);
    return el;
  }

  function imageUrl(url) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + CARD_IMAGE_WIDTH;
  }

  function showCards(products) {
    var list = document.createElement('div');
    list.className = 'searay-chat__cards';
    products.forEach(function (p) {
      var card = document.createElement('a');
      card.className = 'searay-chat__card';
      card.href = '/products/' + encodeURIComponent(p.handle) + '?variant=' + encodeURIComponent(p.variantId);

      if (p.imageUrl) {
        var img = document.createElement('img');
        img.src = imageUrl(p.imageUrl);
        img.alt = '';
        img.loading = 'lazy';
        card.appendChild(img);
      } else {
        var placeholder = document.createElement('span');
        placeholder.className = 'searay-chat__card-placeholder';
        card.appendChild(placeholder);
      }

      var body = document.createElement('span');
      body.className = 'searay-chat__card-body';

      var title = document.createElement('span');
      title.className = 'searay-chat__card-title';
      title.textContent = p.title;
      body.appendChild(title);

      var meta = document.createElement('span');
      meta.className = 'searay-chat__card-meta' + (p.available ? '' : ' searay-chat__card-meta--out');
      meta.textContent = strings.sku + ' ' + p.sku + ' · ' + (p.available ? strings.inStock : strings.outOfStock);
      body.appendChild(meta);

      if (typeof p.price === 'number') {
        var price = document.createElement('span');
        price.className = 'searay-chat__card-price';
        price.textContent = money.format(p.price);
        body.appendChild(price);
      }

      card.appendChild(body);
      list.appendChild(card);
    });
    messages.appendChild(list);
  }

  function linkButton(href, label, secondary) {
    var a = document.createElement('a');
    a.className = 'searay-chat__button' + (secondary ? ' searay-chat__button--secondary' : '');
    a.href = href;
    a.textContent = label;
    return a;
  }

  function showNote(text, links) {
    var note = bubble('note');
    var p = document.createElement('p');
    p.textContent = text;
    note.appendChild(p);
    if (links && links.length) {
      var row = document.createElement('div');
      row.className = 'searay-chat__buttons';
      links.forEach(function (l) {
        row.appendChild(l);
      });
      note.appendChild(row);
    }
    reveal(note);
  }

  function greet() {
    showAssistant(strings.greeting);
  }

  function showThinking() {
    var thinking = bubble('assistant');
    thinking.classList.add('searay-chat__thinking');
    thinking.textContent = strings.thinking;
    scrollToEnd();
    return thinking;
  }

  // A reply from POST /messages, or one picked up from the history: { reply, products, actions, requiresLogin }.
  function showReply(reply) {
    var answer = showAssistant(reply.reply, reply.products);
    if (reply.requiresLogin) {
      showNote(strings.loginPrompt, [linkButton(root.dataset.loginUrl, strings.logIn)]);
    }
    scrollToStart(answer);
    return runActions(reply.actions || []);
  }

  /* ── backend ── */

  function request(path, options) {
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    options = options || {};
    options.signal = controller.signal;
    options.headers = Object.assign({ Accept: 'application/json' }, options.headers);
    options.credentials = 'same-origin';
    return fetch(endpoint + path, options).finally(function () {
      clearTimeout(timer);
    });
  }

  // The conversation's messages, or null when it does not exist (yet) for this shopper.
  function fetchHistory() {
    return request('/conversations/' + encodeURIComponent(conversationId)).then(function (res) {
      if (res.status === 404) return null;
      if (!res.ok) throw new Error('History request failed: ' + res.status);
      return res.json();
    });
  }

  function loadHistory() {
    historyLoaded = true;
    if (!conversationId) {
      greet();
      return;
    }
    var mine = generation;
    fetchHistory()
      .then(function (data) {
        if (mine !== generation) return;
        var messages = (data && data.messages) || [];
        if (!messages.length && !loadPending()) {
          // Expired, or it belongs to another login: start fresh.
          conversationId = null;
          save(localStorage, CONVERSATION_KEY, null);
          greet();
          return;
        }
        if (!messages.length) greet();
        messages.forEach(function (m) {
          if (m.role === ROLE_USER) showUser(m.text);
          else if (m.role === ROLE_ASSISTANT) showAssistant(m.text, m.products);
        });
        scrollToEnd();
        resumePending(messages);
      })
      .catch(function () {
        if (mine === generation) greet();
      });
  }

  // The page changed while a reply was being written. If it has landed since, it was just drawn
  // from the history, but its cart actions still have to run; if not, wait for it here.
  function resumePending(messages) {
    var pending = loadPending();
    if (!pending) return;
    var age = Date.now() - pending.sentAt;
    var reply = replyTo(messages, pending);
    if (reply) {
      savePending(null);
      if (age < PENDING_MAX_AGE_MS) runActions(reply.actions || []);
      return;
    }
    if (age >= REQUEST_TIMEOUT_MS) {
      savePending(null);
      return;
    }
    var last = messages[messages.length - 1];
    if (!last || last.role !== ROLE_USER || last.text !== pending.text) showUser(pending.text);
    setBusy(true);
    var thinking = showThinking();
    waitForReply(thinking, pending)
      .catch(function () {
        showError(thinking);
      })
      .finally(function () {
        // No focus: on a phone that would pop the keyboard up over the page just loaded.
        savePending(null);
        setBusy(false);
      });
  }

  // Looks for the reply in the history until it is due.
  function waitForReply(thinking, pending) {
    function retry(err) {
      if (Date.now() - pending.sentAt >= REQUEST_TIMEOUT_MS) throw err;
      return waitForReply(thinking, pending);
    }
    return new Promise(function (resolve) {
      setTimeout(resolve, POLL_INTERVAL_MS);
    })
      .then(fetchHistory)
      .then(function (data) {
        var reply = data && replyTo(data.messages, pending);
        if (!reply) return retry(new Error('No reply yet'));
        thinking.remove();
        return showReply({ reply: reply.text, products: reply.products, actions: reply.actions });
      }, retry);
  }

  function setBusy(value) {
    busy = value;
    send.disabled = value;
    input.readOnly = value;
  }

  function showError(thinking) {
    thinking.remove();
    showAssistant(strings.error);
    scrollToEnd();
  }

  function done() {
    savePending(null);
    setBusy(false);
    input.focus();
  }

  function submit(text) {
    if (busy || !text) return;
    setBusy(true);
    showUser(text);
    var thinking = showThinking();

    if (!conversationId) {
      conversationId = newConversationId();
      save(localStorage, CONVERSATION_KEY, conversationId);
    }
    var pending = { text: text, sentAt: Date.now() };
    savePending(pending);
    var answered = false;

    request('/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // tierPricing: this page shows the customer's own tier prices, so the chat quotes them too.
      body: JSON.stringify({ conversationId: conversationId, message: text, tierPricing: root.dataset.tierPricing === 'true' }),
    })
      .then(function (res) {
        answered = true;
        if (!res.ok) throw new Error('Chat request failed: ' + res.status);
        return res.json();
      })
      .then(function (reply) {
        thinking.remove();
        // The backend starts afresh under its own id if this one belongs to another login.
        if (reply.conversationId && reply.conversationId !== conversationId) {
          conversationId = reply.conversationId;
          save(localStorage, CONVERSATION_KEY, conversationId);
        }
        return showReply(reply);
      })
      .catch(function (err) {
        // No answer at all (the connection dropped or timed out): the backend still finishes the turn.
        if (!answered) return waitForReply(thinking, pending);
        throw err;
      })
      .catch(function () {
        showError(thinking);
      })
      .finally(done);
  }

  /* ── cart ── */

  function runActions(actions) {
    var adds = actions.filter(function (a) {
      return a.type === ACTION_ADD_TO_CART;
    });
    if (!adds.length) return Promise.resolve();

    // One at a time, so one sold-out item does not stop the others.
    return adds
      .reduce(function (chain, action) {
        return chain.then(function (done) {
          return addToCart(action).then(
            function () {
              return done.concat(action);
            },
            function (reason) {
              showNote(strings.addFailed.replace('[sku]', action.sku).replace('[reason]', reason));
              return done;
            },
          );
        });
      }, Promise.resolve([]))
      .then(function (done) {
        if (!done.length) return;
        var summary = done
          .map(function (a) {
            return a.quantity + ' × ' + a.sku;
          })
          .join(', ');
        showNote(strings.added + ' ' + summary, [
          linkButton(root.dataset.cartUrl, strings.viewCart, true),
          linkButton(CHECKOUT_URL, strings.checkout),
        ]);
      });
  }

  function addToCart(action) {
    return fetch(root.dataset.cartAddUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(action.variantId), quantity: action.quantity }],
        sections: CART_BUBBLE_SECTION,
      }),
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw body.description || body.message || res.statusText;
          return body;
        });
      })
      .then(function (body) {
        updateCartBubble(body.sections && body.sections[CART_BUBBLE_SECTION]);
        document.dispatchEvent(new CustomEvent('searay-chat:cart-updated', { detail: action }));
      });
  }

  // Same swap Dawn's cart notification does after an add, so the header count is right.
  function updateCartBubble(html) {
    var target = document.getElementById(CART_BUBBLE_SECTION);
    if (!target || !html) return;
    var section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (section) target.innerHTML = section.innerHTML;
  }

  /* ── wiring ── */

  function fitInput() {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, MAX_INPUT_HEIGHT_PX) + 'px';
    input.style.overflowY = input.scrollHeight > MAX_INPUT_HEIGHT_PX ? 'auto' : 'hidden';
  }

  launcher.addEventListener('click', function () {
    open(true);
  });
  root.querySelector('[data-searay-chat-close]').addEventListener('click', close);
  root.querySelector('[data-searay-chat-reset]').addEventListener('click', function () {
    if (!busy) reset();
  });

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    var text = input.value.trim();
    if (!text) return;
    input.value = '';
    fitInput();
    submit(text);
  });

  input.addEventListener('input', fitInput);
  input.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      form.requestSubmit();
    }
  });

  panel.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') close();
  });

  // Reopen after a page change in the same tab (say, after following a product card), without
  // grabbing focus, which on a phone would pop the keyboard up over the page.
  if (load(sessionStorage, OPEN_KEY)) open(false);
})();
